import { storeToRefs } from "pinia";
import { CatalogData } from "ecom-types";

/**
 * Payload data for wishlist API operations (add/update)
 */
export interface WishListPayloadData {
  sku: string | null | undefined;
  vendor_model: string | null | undefined;
  options?: Record<string, any>;
}

/**
 * Wishlist item type (extends CatalogData with wishlist-specific fields)
 */
export type WishlistItem = CatalogData & {
  wishlist_item_id?: number;
};

/**
 * Wishlist data structure (API response)
 */
export interface WishListData {
  unique_id: string;
  items: WishlistItem[];
}

// Global loading states - shared across all instances of useWishlistV2
// Prevents parallel add/remove operations
const addToWishlistLoading = ref(false);
const removeFromWishlistLoading = ref(false);

/**
 * Wishlist V2 Composable
 * 
 * Similar to useCart composable pattern - provides computed properties and methods
 * for managing wishlist state from Pinia store.
 * 
 * Uses new wishlistV2 API module with improved endpoints:
 * - POST /wishlist/items - Add item to wishlist
 * - DELETE /wishlist/items/:id - Delete item from wishlist
 * 
 * API Response Structure:
 * {
 *   unique_id: string,
 *   items: [
 *     {
 *       id: number,                    // Product ID
 *       wishlist_item_id: number,      // Wishlist item ID (use for deletion)
 *       sku: string,
 *       vendor_model: string,
 *       ...other product fields
 *     }
 *   ]
 * }
 * 
 * Features:
 * - Computed properties for reactive data access (wishlistInfo, wishlistItems)
 * - Better error handling with skipErrors
 * - Support for deleteItem operation
 * - Uses wishlist_unique_id parameter
 * - Works with new items array structure
 */
export const useWishlistV2 = () => {
  const generalStore = useGeneralStore();
  const generalInternalStore = useGeneralInternalStore();
  const { wishlist } = storeToRefs(generalStore);
  const { $api } = useNuxtApp();
  const { wishlistId } = useCookiesService();
  const { addToFavoritesEvent, removeFromFavoritesEvent } = useEvents();
  const { openSystemDialog } = useSystemDialogs();
  const { t } = useI18n();

  /**
   * Get wishlist info from store
   * V2 API structure: { unique_id, items: [...] }
   */
  const wishlistInfo = computed((): WishListData | null => {
    return (wishlist.value as any) as WishListData | null;
  });

  /**
   * Get wishlist items array
   * V2 API structure: items array contains full product objects
   */
  const wishlistItems = computed(() => {
    return (wishlistInfo.value as any)?.items || [];
  });

  /**
   * Check if item exists in wishlist
   * V2 API: checks by configuration_id (from selected_config, variant_plp_image, or default_config) and vendor_model
   * API returns CatalogData structure where the sent SKU is stored in selected_config.configuration_id
   * vendor_model determines the product, sku determines the product variant
   */
  const inWishlist = computed(() => {
    return (payload: Pick<WishListPayloadData, "sku" | "vendor_model">) => {
      if (!payload.sku || !wishlistItems.value?.length) return undefined;
      
      return wishlistItems.value.find((el: WishlistItem) => {
        const itemSku = getItemSku(el);
        return itemSku === payload.sku && el.vendor_model === payload.vendor_model;
      });
    };
  });

  /**
   * Validate wishlist operation prerequisites
   * Checks authentication and wishlist ID availability
   */
  function validateWishlistOperation(): boolean {
    if (!generalStore.isAuthenticated) {
      generalInternalStore.isSignInDrawerOpen = true;
      return false;
    }
    return !!wishlistId.value;
  }

  /**
   * Get SKU from wishlist item
   * Priority: selected_config.configuration_id > variant_plp_image.sku > default_config.configuration_id > default_sku
   */
  function getItemSku(item: WishlistItem): string | undefined {
    return (
      item.selected_config?.configuration_id || 
      item.variant_plp_image?.sku || 
      item.default_config?.configuration_id || 
      item.default_sku
    );
  }

  /**
   * Find item in wishlist by sku and vendor_model
   * Helper function to get item data from wishlistItems
   */
  function findWishlistItem(sku: string, vendorModel: string): WishlistItem | undefined {
    return wishlistItems.value.find((el: WishlistItem) => {
      const itemSku = getItemSku(el);
      return itemSku === sku && el.vendor_model === vendorModel;
    });
  }

  /**
   * Prepare event data from wishlist item
   * Common function for both add and remove events
   */
  function prepareEventData(item: WishlistItem) {
    const itemSku = getItemSku(item);
    // Calculate product price using the same logic as useProduct composable
    const productPrice = Number(
      item.selected_config?.cost ||
        item.variant_plp_image?.cost ||
        item.default_config?.cost ||
        item.min_cost ||
        item.cost ||
        item.base_cost ||
        0,
    );
    return {
      product_id: item.id?.toString(),
      configuration_id: itemSku,
      product_sku: item.vendor_model,
      product_price: productPrice,
      collection_id: item.collections_ids?.[0],
      product_title: item.currentTranslation?.title || item.selected_config?.currentTranslation?.title,
      product_configuration_id: item.selected_config?.id || item.default_config?.id,
    } as any;
  }


  /**
   * Manually remove item from wishlist when API doesn't return updated data
   * Filters out the item by wishlist_item_id or by sku + vendor_model
   */
  function removeItemFromWishlistManually(itemId: number | string, sku?: string, vendorModel?: string) {
    const currentWishlist = wishlist.value as any;
    if (!currentWishlist?.items?.length) return;

    const filteredItems = currentWishlist.items.filter((el: WishlistItem) => {
      const itemSku = getItemSku(el);
      
      if (sku && vendorModel) {
        return !(itemSku === sku && el.vendor_model === vendorModel);
      }
      return el.wishlist_item_id !== itemId;
    });
    
    wishlist.value = {
      ...currentWishlist,
      items: filteredItems,
    } as any;
  }

  /**
   * Add item to wishlist
   * Uses V2 API endpoint: POST /wishlist/items
   * Updates store with response data and sends analytics event
   */
  async function addToWishlist(payload: WishListPayloadData) {
    if (addToWishlistLoading.value || !validateWishlistOperation() || !payload.sku) return;

    const itemData: any = {
      sku: payload.sku,
      vendor_model: payload.vendor_model,
      wishlist_unique_id: wishlistId.value,
      options: payload.options || {},
    };

    addToWishlistLoading.value = true;
    try {
      const { data } = await $api.wishlistV2.addItem(itemData);
      if (data) {
        wishlist.value = data;
        
        // Find added item in wishlistItems to get full data for event
        const addedItem = findWishlistItem(payload.sku, payload.vendor_model);
        if (addedItem) {
          addToFavoritesEvent(prepareEventData(addedItem));
        }
      }
    } catch (error: any) {
      console.error("Error adding item to wishlist:", error);
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: error?.data?.errors?.[0]?.message || "Server error",
      });
      throw error;
    } finally {
      addToWishlistLoading.value = false;
    }
  }

  /**
   * Add or remove item from wishlist
   * Wrapper function that calls addToWishlist or removeFromWishlist based on item existence
   */
  async function updateWishlist(payload: WishListPayloadData) {
    if (!validateWishlistOperation() || !payload.sku) return;
    
    const isExist = inWishlist.value(payload);

    // If item exists, remove it
    if (isExist) {
      const existingItem = findWishlistItem(payload.sku, payload.vendor_model);
      
      const wishlistItemId = existingItem?.wishlist_item_id;
      if (wishlistItemId) {
        await removeFromWishlist(wishlistItemId, payload.sku, payload.vendor_model);
        return;
      }
      console.warn("Item exists in wishlist but no wishlist_item_id found for deletion", existingItem);
      return;
    }
    
    // If item doesn't exist, add it
    await addToWishlist(payload);
  }

  /**
   * Remove item from wishlist by wishlist_item_id
   * Uses V2 API endpoint: DELETE /wishlist/items/:id
   * Updates store with response data and sends analytics event
   * 
   * @param itemId - Wishlist item ID (wishlist_item_id from items array) to remove
   * @param sku - SKU of the item to remove (for finding item data if API doesn't return data)
   * @param vendorModel - Vendor model of the item to remove (for finding item data if API doesn't return data)
   */
  async function removeFromWishlist(itemId: number | string, sku?: string, vendorModel?: string) {
    if (removeFromWishlistLoading.value || !validateWishlistOperation() || !itemId) {
      if (!wishlistId.value) {
        console.warn("Wishlist ID is not available");
      }
      if (!itemId) {
        console.warn("Item ID is required to remove from wishlist");
      }
      return;
    }

    // Store wishlistId after validation
    const currentWishlistId = wishlistId.value!;

    // Find item before deletion to get data for event
    let itemToRemove: WishlistItem | undefined;
    if (sku && vendorModel) {
      itemToRemove = findWishlistItem(sku, vendorModel);
    } else {
      // Fallback: find by wishlist_item_id
      itemToRemove = wishlistItems.value.find((el: WishlistItem) => 
        el.wishlist_item_id === itemId
      );
    }

    removeFromWishlistLoading.value = true;
    try {
      await $api.wishlistV2.deleteItem(
        itemId,
        currentWishlistId,
      );
      
      // Manually remove item from wishlist
      if (itemToRemove) {
        removeItemFromWishlistManually(itemId, sku, vendorModel);
      }
      
      // Synchronize wishlist with server
      const { data } = await $api.wishlistV2.getWishList(currentWishlistId);
      if (data) {
        wishlist.value = data;
      }
      
      // Send event after successful removal
      if (itemToRemove) {
        removeFromFavoritesEvent(prepareEventData(itemToRemove));
      }
    } catch (error: any) {
      console.error("Error removing item from wishlist:", error);
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: error?.data?.errors?.[0]?.message || "Server error",
      });
      throw error;
    } finally {
      removeFromWishlistLoading.value = false;
    }
  }

  return {
    wishlistInfo,
    wishlistItems,
    inWishlist,
    updateWishlist,
    addToWishlist,
    removeFromWishlist,
    addToWishlistLoading,
    removeFromWishlistLoading,
    getItemSku,
  };
};
